import type { ComponentPropsWithRef } from 'react';
import { cn } from '@/lib/utils';
import { Icon } from '@/shared-ui/atoms/icon';
import { Typography } from '@/shared-ui/atoms/typography';
import { CloseIcon } from '@/shared-ui/icons';
import { Slot } from '@radix-ui/react-slot';

export type AlertVariant = 'success' | 'warning' | 'error' | 'custom';

type RootProps = ComponentPropsWithRef<'div'> & {
    variant: AlertVariant;
};

const rootVariantClasses: Record<AlertVariant, string> = {
    success: 'bg-success-foreground',
    warning: 'bg-warning-foreground',
    error: 'bg-destructive-foreground',
    custom: 'bg-snk-cream',
};

const Root = ({ className, variant, ref, ...props }: RootProps) => (
    <div
        data-slot="alert"
        data-variant={variant}
        ref={ref}
        className={cn('flex gap-2 rounded-sm p-2 text-foreground w-full', rootVariantClasses[variant], className)}
        {...props}
    />
);
Root.displayName = 'Alert.Root';

type AlertIconWrapperProps = ComponentPropsWithRef<'div'> & {
    asChild?: boolean;
};

const IconWrapper = ({ className, children, asChild, ...props }: AlertIconWrapperProps) => {
    const Comp = asChild ? Slot : 'div';
    return (
        <Comp className={cn('w-4 h-full flex items-center', className)} {...props}>
            {children}
        </Comp>
    );
};
IconWrapper.displayName = 'Alert.IconWrapper';

type TextProps = Omit<ComponentPropsWithRef<typeof Typography>, 'variant' | 'weight' | 'as'>;

const Text = ({ className, ref, ...props }: TextProps) => (
    <Typography
        data-slot="alert-text"
        as="p"
        variant="caption"
        weight="light"
        className={cn('flex-1', className)}
        ref={ref as never}
        {...props}
    />
);
Text.displayName = 'Alert.Text';

type CloseButtonProps = ComponentPropsWithRef<'button'>;

const CloseButton = ({ className, type = 'button', ...props }: CloseButtonProps) => (
    <button data-slot="alert-close-button" type={type} className={cn('shrink-0', className)} {...props}>
        <Icon icon={CloseIcon} className="w-[12.3px] h-[12.3px]" />
    </button>
);
CloseButton.displayName = 'Alert.CloseButton';

const Alert = { Root, IconWrapper, Text, CloseButton } as const;

export { Alert };
